import React, { useEffect, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import FilterDropdown from "./FilterDropdown";
import axios from "axios";
import DataTable from "react-data-table-component";
import Spinner from "../components/ui/Spinner";
import {
  ArrowDownLeft,
  ArrowUpRight,
  Clock,
  Loader2,
  Headset,
  Phone
} from "lucide-react";

// API Imports
import {
  fetchCallDetail,
  fetchPieChartData,
  fetchTopUsersData,
  processChartData,
} from "../components/Dashboard/api";
import {
  createMultiAxisChartOptions,
  createPieChartOptions,
  createTopUsersPieChartOptions,
} from "./ChartOptions";
import { DashboardStatsView } from "./Dashboard/DashboardStats";
import { BASE_API, CallGETAPI } from "../helper/Constants";

/* -----------------------------------------------------------------------
   1. INTERNAL COMPONENT: UnifiedDurationStats
   (Defined here to avoid import/export errors)
   ----------------------------------------------------------------------- */
const formatDuration = (duration) => {
  if (!duration) return '0h 0m 0s';
  const hours = Math.floor(duration / 3600);
  const minutes = Math.floor((duration % 3600) / 60);
  const seconds = duration % 60;
  return `${hours}h ${minutes}m ${seconds}s`;
};

const UnifiedDurationStats = ({ startDate, endDate }) => {
  const [stats, setStats] = useState({
    extension_duration: 0,
    callprocessed_duration: 0,
    total_duration: 0,
    tollfree_duration: 0,
    did_duration: 0,
  });
  
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchAllData = async () => {
      if (!startDate || !endDate) return;

      setLoading(true);
      try {
        const [generalResponse, tollFreeResponse] = await Promise.all([
          CallGETAPI(`api/get-monthly-duration?start_date=${startDate}&end_date=${endDate}`),
          CallGETAPI(`api/get-toll-free-monthly-duration?start_date=${startDate}&end_date=${endDate}`)
        ]);

        let generalTotals = { extension_duration: 0, callprocessed_duration: 0, total_duration: 0 };
        if (generalResponse?.data?.success && generalResponse.data.data.length > 0) {
          generalTotals = generalResponse.data.data.reduce((acc, curr) => ({
            extension_duration: acc.extension_duration + (curr.extension_duration || 0),
            callprocessed_duration: acc.callprocessed_duration + (curr.callprocessed_duration || 0),
            total_duration: acc.total_duration + (curr.total_duration || 0)
          }), generalTotals);
        }

        let tollFreeTotals = { tollfree_duration: 0, did_duration: 0 };
        if (tollFreeResponse?.data?.success && tollFreeResponse.data.data.length > 0) {
          tollFreeTotals = tollFreeResponse.data.data.reduce((acc, curr) => ({
            tollfree_duration: acc.tollfree_duration + (curr.tollfree_duration || 0),
            did_duration: acc.did_duration + (curr.did_duration || 0),
          }), tollFreeTotals);
        }

        setStats({ ...generalTotals, ...tollFreeTotals });

      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [startDate, endDate]);

  const StatCard = ({ label, value, icon: Icon, colorClass, bgClass }) => (
    <div className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-slate-100 dark:border-slate-700 shadow-sm flex items-center gap-4 transition-all hover:shadow-md h-full">
      <div className={`p-3 rounded-lg ${bgClass}`}>
        <Icon size={24} className={colorClass} />
      </div>
      <div>
        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
          {label}
        </p>
        <div className="mt-1">
          {loading ? (
            <div className="flex items-center gap-2 text-slate-400">
              <Loader2 size={16} className="animate-spin" />
              <span className="text-sm">Loading...</span>
            </div>
          ) : (
            <h4 className="text-lg font-bold text-slate-800 dark:text-white">
              {formatDuration(value)}
            </h4>
          )}
        </div>
      </div>
    </div>
  );

  const statCardsConfig = [
    { label: "Inbound Duration", value: stats.extension_duration, icon: ArrowDownLeft, bgClass: "bg-emerald-50 dark:bg-emerald-900/20", colorClass: "text-emerald-600 dark:text-emerald-400" },
    { label: "Outbound Duration", value: stats.callprocessed_duration, icon: ArrowUpRight, bgClass: "bg-amber-50 dark:bg-amber-900/20", colorClass: "text-amber-600 dark:text-amber-400" },
    { label: "TollFree Duration", value: stats.tollfree_duration, icon: Headset, bgClass: "bg-blue-50 dark:bg-blue-900/20", colorClass: "text-blue-600 dark:text-blue-400" },
    { label: "DID Duration", value: stats.did_duration, icon: Phone, bgClass: "bg-purple-50 dark:bg-purple-900/20", colorClass: "text-purple-600 dark:text-purple-400" },
    { label: "Total Duration", value: stats.total_duration, icon: Clock, bgClass: "bg-indigo-50 dark:bg-indigo-900/20", colorClass: "text-indigo-600 dark:text-indigo-400" }
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-6">
      {statCardsConfig.map((card, index) => (
        <StatCard key={index} {...card} />
      ))}
    </div>
  );
};

/* -----------------------------------------------------------------------
   2. MAIN DASHBOARD COMPONENT
   ----------------------------------------------------------------------- */
const getChartOptions = (options) => {
  const newOptions = {
    ...options,
    chart: { ...options.chart, backgroundColor: "transparent", style: { fontFamily: "inherit" } },
    title: { ...options.title, style: { color: "var(--chart-text-color, #374151)" } },
    legend: { ...options.legend, itemStyle: { color: "var(--chart-text-color, #374151)" } },
  };

  if (options.xAxis) {
    const xAxes = Array.isArray(options.xAxis) ? [...options.xAxis] : [options.xAxis];
    newOptions.xAxis = xAxes.map((axis) => ({
      ...axis,
      labels: { ...(axis?.labels || {}), style: { color: "var(--chart-text-color, #9ca3af)" } },
    }));
  }

  if (options.yAxis) {
    let yAxes = Array.isArray(options.yAxis) ? [...options.yAxis] : [options.yAxis];
    const requiresSecondAxis = options.series && options.series.some((s) => s.yAxis === 1);
    if (requiresSecondAxis && yAxes.length < 2) {
      yAxes.push({ title: { text: "" }, labels: { enabled: false }, visible: false });
    }
    newOptions.yAxis = yAxes.map((axis) => ({
      ...axis,
      labels: { ...(axis?.labels || {}), style: { color: "var(--chart-text-color, #9ca3af)" } },
      title: { ...(axis?.title || {}), style: { color: "var(--chart-text-color, #374151)" } },
    }));
  }
  return newOptions;
};

function Dashboard() {
  const [chartData, setChartData] = useState([]);
  const [pieChartData, setPieChartData] = useState([]);
  const [topUsersData, setTopUsersData] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [filteredStats, setFilteredStats] = useState({ totalCalls: 0, answeredCalls: 0, cancelCalls: 0, missCalls: 0, totalProfit: 0 });
  const [transactionData, setTransactionData] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(true);

  useEffect(() => {
    const currentDate = new Date();
    const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    setStartDate(startOfMonth.toISOString().split("T")[0]);
    setEndDate(currentDate.toISOString().split("T")[0]);
  }, []);

  useEffect(() => {
    if (startDate && endDate) loadDashboardData(startDate, endDate);
  }, [startDate, endDate]);

  const loadDashboardData = async (start, end) => {
    setLoading(true);
    try {
      const [callDetailResponse, pieChartDataResponse, topUsersDataResponse] = await Promise.all([
        fetchCallDetail(start, end),
        fetchPieChartData(start, end),
        fetchTopUsersData(start, end),
      ]);

      if (callDetailResponse) {
        setChartData(processChartData(callDetailResponse.data, start, end));
        const { totals } = callDetailResponse;
        if (totals) {
          setFilteredStats({
            totalCalls: parseInt(totals.incoming) || 0,
            answeredCalls: parseInt(totals.answer) || 0,
            cancelCalls: parseInt(totals.cancelcalls) || 0,
            missCalls: parseInt(totals.miscall) || 0,
            totalProfit: parseFloat(totals.total_margin) || 0,
          });
        }
      }
      setPieChartData(pieChartDataResponse);
      setTopUsersData(topUsersDataResponse);
    } catch (error) {
      console.error("Error loading dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchTransactions = async () => {
      setLoadingTransactions(true);
      try {
        const response = await axios.get(BASE_API + "api/admin/balance-transactions", { timeout: 20000 });
        const filteredData = (response?.data?.data || [])
          .filter((item) => item.transaction_type === "Credit" && item.status === "completed" && item.description === "Balance Updated Successfully")
          .map(({ date, user_name, amount, user_id, user_email }) => ({ date, user_name, amount, user_id, user_email }));
        setTransactionData(filteredData);
      } catch (error) {
        console.error("Error fetching transactions:", error);
      } finally {
        setLoadingTransactions(false);
      }
    };
    fetchTransactions();
  }, []);

  const handleFilterChange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
  };

  const customStyles = {
    table: { style: { backgroundColor: "transparent" } },
    headRow: { style: { backgroundColor: "var(--table-header-bg, #f3f4f6)", borderBottomWidth: "1px", borderColor: "var(--table-border-color, #e5e7eb)", minHeight: "52px" } },
    headCells: { style: { fontSize: "0.875rem", fontWeight: "600", color: "var(--table-text-color, #374151)", textTransform: "uppercase", letterSpacing: "0.05em", paddingLeft: "16px", paddingRight: "16px" } },
    rows: { style: { fontSize: "0.875rem", backgroundColor: "transparent", color: "var(--table-text-color, #374151)", minHeight: "60px", "&:not(:last-of-type)": { borderBottomStyle: "solid", borderBottomWidth: "1px", borderBottomColor: "var(--table-border-color, #e5e7eb)" }, "&:hover": { backgroundColor: "var(--table-row-hover, rgba(0,0,0,0.02))" } } },
    pagination: { style: { backgroundColor: "transparent", color: "var(--table-text-color, #374151)", fontSize: "0.875rem" } },
  };

  const columns = [
    { name: "Date", selector: (row) => row.date },
    { name: "User Id", selector: (row) => row.user_id },
    { name: "User Email", selector: (row) => row.user_email },
    { name: "Account", selector: (row) => row.user_name },
    { name: "Amount", selector: (row) => row.amount, width: "120px", cell: (row) => (<span className="font-medium text-green-600 dark:text-green-400">${row.amount}</span>) },
  ];

  return (
    <main className="w-full min-h-screen p-4 md:p-6 lg:p-8 bg-gray-50 dark:bg-gray-900 transition-colors duration-300 text-gray-800 dark:text-gray-100">
      <style>{`:root{--chart-text-color:#374151;--table-text-color:#374151;--table-header-bg:#f3f4f6;--table-border-color:#e5e7eb;--table-row-hover:rgba(0,0,0,0.02)}.dark{--chart-text-color:#e5e7eb;--table-text-color:#e5e7eb;--table-header-bg:#1f2937;--table-border-color:#374151;--table-row-hover:rgba(255,255,255,0.05)}`}</style>
      
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">Dashboard</h1>
          <nav className="flex mt-2 text-sm text-gray-500 dark:text-gray-400">
            <a href="/" className="hover:text-blue-500 transition-colors">Home</a><span className="mx-2">/</span><span className="text-blue-600 dark:text-blue-400 font-medium">Dashboard</span>
          </nav>
        </div>
        <div className="w-full md:w-auto z-10">
          <FilterDropdown onFilterChange={handleFilterChange} loading={loading} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-12 space-y-6">
          <div className="w-full">
            <UnifiedDurationStats startDate={startDate} endDate={endDate} />
          </div>
          <div className="w-full">
            <DashboardStatsView stats={filteredStats} />
          </div>
        </div>

        {loading ? (
          <div className="lg:col-span-12 flex justify-center py-20"><Spinner animation="border" className="w-10 h-10 text-blue-600" /></div>
        ) : (
          <>
            <div className="lg:col-span-12">
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 transition-colors">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-6">Call Data Overview <span className="text-sm font-normal text-gray-500">(Daily)</span></h3>
                {chartData.length > 0 ? <HighchartsReact highcharts={Highcharts} options={getChartOptions(createMultiAxisChartOptions(chartData, startDate))} /> : <div className="h-64 flex items-center justify-center text-gray-400">No Data Found</div>}
              </div>
            </div>
            <div className="lg:col-span-6">
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 h-full transition-colors">
                <h5 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Extension Call</h5>
                {pieChartData.length > 0 ? <HighchartsReact highcharts={Highcharts} options={getChartOptions(createPieChartOptions(pieChartData))} /> : <div className="h-64 flex items-center justify-center text-gray-400">No Data Found</div>}
              </div>
            </div>
            <div className="lg:col-span-6">
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 h-full transition-colors">
                <h5 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Top Users by Duration</h5>
                <HighchartsReact highcharts={Highcharts} options={getChartOptions(createTopUsersPieChartOptions(topUsersData, startDate, endDate))} />
              </div>
            </div>
          </>
        )}

        <div className="lg:col-span-12">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 transition-colors">
            <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-6">Refill History</h2>
            {loadingTransactions ? <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div> : <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700"><DataTable columns={columns} data={transactionData} pagination customStyles={customStyles} className="dark:text-gray-300" /></div>}
          </div>
        </div>
      </div>
    </main>
  );
}

export default Dashboard;